// Every published post under /blog, keyed by its URL slug.
//
// To publish a new one: write `src/content/blog/<slug>.md`, add a line to
// `blogPosts` below, then add the slug to `CARDS` in
// `scripts/og-card/generate.ts` and run `npm run generate:og-cards`. Nothing
// else needs touching — the route's `head`, the page, the sitemap and the
// /blog index all read this map, and the index orders itself by the post's own
// frontmatter date (see `blogIndex` below), so the line's position here is
// immaterial.
//
// The markdown is parsed at import time, so a post's title and description are
// available to the route's `head` at match time without pulling the rendering
// component — styles, section cards, newsletter form — into that work.

import july2026 from 'content/blog/july-2026-updates.md?raw'
import newLook from 'content/blog/new-look-for-thai2english.md?raw'
import { isoDate } from 'lib/seo'
import { type Meta, type Post, parsePost } from './post-parser'

export const blogPosts: Record<string, Post> = {
   'july-2026-updates': parsePost(july2026),
   'new-look-for-thai2english': parsePost(newLook),
}

export const blogSlugs = Object.keys(blogPosts)

export const getBlogPost = (slug: string): Post | undefined => blogPosts[slug]

/** A post as the /blog index lists it: everything but the body. */
export type PostSummary = { slug: string; meta: Meta }

/**
 * Every post newest first — the order the /blog index lists them in, and so
 * `blogIndex[0]` is the featured one.
 *
 * Sorted on each post's own frontmatter date rather than the order they happen
 * to be registered above, so adding one out of order can't misorder the feed.
 * A date `isoDate` doesn't recognise sorts last rather than jumping the queue.
 */
export const blogIndex: PostSummary[] = Object.entries(blogPosts)
   .map(([slug, post]) => ({ slug, meta: post.meta }))
   .sort((a, b) => (isoDate(b.meta.date) ?? '').localeCompare(isoDate(a.meta.date) ?? ''))
